/**
 * ChatIaPage.tsx — Tela "Conversar com a CROSS" (F3 — reconstrucao visual fiel ao prototipo).
 *
 * ISOLAMENTO CRITICO (ver instrucao F3):
 * - Importa screen-chat.css exclusivo (nao edita global.css).
 * - Usa ChatBubble (novo, F3) em vez de ChatMessage (F2, compartilhado).
 * - Toda logica de envio/retry/fontes/fallback preservada integralmente.
 * - Zero string hardcoded (tudo via t()).
 *
 * Layout: cs-shell (full-height) > cs-topbar + cs-thread (scroll) + cs-input-bar (fixo).
 * Referencia: docs/execucao-projetos/_local/design/prototipo/chat.html
 */

import '../styles/screen-chat.css';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { ChatBubble, TypingBubble } from '../components/ChatBubble';
import { type ChatMessageData, type ChatSource } from '../components/ChatMessage';
import { apiClient } from '../lib/api-client';

// ---------------------------------------------------------------------------
// Tipos locais
// ---------------------------------------------------------------------------
interface ChatSession {
  readonly id: string;
  readonly title: string;
  readonly updatedAt: string;
}

interface SendResponse {
  readonly message: ChatMessageData;
}

// ---------------------------------------------------------------------------
// ChatIaPage
// ---------------------------------------------------------------------------
export function ChatIaPage(): JSX.Element {
  const [_sessions, setSessions] = useState<readonly ChatSession[]>([]);
  const [activeSession, setActiveSession] = useState<string | null>(null);
  const [messages, setMessages] = useState<readonly ChatMessageData[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [activeRef, setActiveRef] = useState<string | null>(null);
  const threadRef = useRef<HTMLElement>(null);
  const localMsgCounter = useRef(0);
  const navigate = useNavigate();

  // --- Carregar sessoes ---
  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const data = await apiClient.get<{ sessions: readonly ChatSession[] }>('/chat-ia/sessions');
        if (cancelled) return;
        const list = Array.isArray(data?.sessions) ? data.sessions : [];
        setSessions(list);
        if (list.length > 0 && activeSession === null) {
          setActiveSession(list[0]?.id ?? null);
        }
      } catch {
        if (!cancelled) setSessions([]);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Carregar mensagens da sessao ativa ---
  useEffect(() => {
    if (activeSession === null) {
      setMessages([]);
      return;
    }
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const data = await apiClient.get<{ messages: readonly ChatMessageData[] }>(
          `/chat-ia/sessions/${activeSession}/messages`,
        );
        if (cancelled) return;
        const list = Array.isArray(data?.messages) ? data.messages : [];
        setMessages(list);
      } catch {
        if (!cancelled) setMessages([]);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [activeSession]);

  // --- Auto-scroll para o final da thread ---
  useEffect(() => {
    const el = threadRef.current;
    if (el && typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight });
    } else if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, sending]);

  // --- Nova sessao (F1-003: botao visivel no topo da tela) ---
  const handleNewSession = (): void => {
    setActiveSession(null);
    setMessages([]);
  };

  /**
   * Retry — resubmete a mensagem do usuario apos erro.
   * Remove a bolha de erro e tenta novamente; preserva o conteudo do usuario.
   */
  const retryMessage = useCallback(
    async (userContent: string, errorMsgId: string): Promise<void> => {
      if (sending) return;
      setSending(true);
      setMessages((prev) => prev.filter((m) => m.id !== errorMsgId));

      try {
        const response = await apiClient.post<SendResponse>('/chat-ia/messages', {
          session_id: activeSession,
          content: userContent,
        });
        setMessages((prev) => [...prev, response.message]);
      } catch {
        localMsgCounter.current += 1;
        const errMsg: ChatMessageData = {
          id: `err-${localMsgCounter.current}`,
          role: 'assistant',
          content: '',
          isError: true,
        };
        setMessages((prev) => [...prev, errMsg]);
      } finally {
        setSending(false);
      }
    },
    [sending, activeSession],
  );

  /**
   * sendMessage — envia mensagem e aguarda resposta.
   * Insere bolha do usuario imediatamente; exibe TypingBubble via `sending`;
   * insere bolha de erro se falhar (product-spec §4.4).
   */
  const sendMessage = useCallback(
    async (content: string): Promise<void> => {
      if (content.length === 0 || sending) return;

      setSending(true);
      setActiveRef(null);

      localMsgCounter.current += 1;
      const userMessage: ChatMessageData = {
        id: `local-${localMsgCounter.current}`,
        role: 'user',
        content,
      };
      setMessages((prev) => [...prev, userMessage]);
      setInput('');

      try {
        const response = await apiClient.post<SendResponse>('/chat-ia/messages', {
          session_id: activeSession,
          content,
        });
        setMessages((prev) => [...prev, response.message]);
      } catch {
        localMsgCounter.current += 1;
        const errMsg: ChatMessageData = {
          id: `err-${localMsgCounter.current}`,
          role: 'assistant',
          content: '',
          isError: true,
        };
        setMessages((prev) => [...prev, errMsg]);
      } finally {
        setSending(false);
      }
    },
    [sending, activeSession],
  );

  // --- Handlers ---
  const handleSend = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    await sendMessage(input.trim());
  };

  // F1-005: sugestoes preenchem o input e aguardam submissao (nao disparam imediatamente).
  const handleSuggestion = (suggestion: string): void => {
    setInput(suggestion);
  };

  const handleSourceClick = (source: ChatSource): void => {
    setActiveRef((prev) => (prev === source.id ? null : source.id));
  };

  const handleConnectSources = (): void => {
    void navigate('/sources');
  };

  /** handleRetry — estavel, sem closure inline no render. */
  const handleRetry = useCallback(
    (errorMsgId: string): void => {
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === errorMsgId);
        if (idx <= 0) return prev;
        const userMsg = prev[idx - 1];
        const content = userMsg?.role === 'user' ? userMsg.content : '';
        if (content.length > 0) void retryMessage(content, errorMsgId);
        return prev;
      });
    },
    [retryMessage],
  );

  const SUGGESTIONS = [t('chat.suggestion1'), t('chat.suggestion2'), t('chat.suggestion3')];

  const isEmpty = messages.length === 0 && !sending;

  return (
    /*
     * cs-shell cancela o padding do app-main (margin negativa)
     * para que a tela de chat ocupe de borda a borda, como no prototipo.
     *
     * Topbar removida daqui: o header global do AppLayout (header.topbar)
     * ja e o unico header da tela. O cs-topbar proprio gerava header duplicado
     * (D-1 do relatorio de fidelidade chat.md 2026-06-04).
     */
    <section className="cs-shell" aria-labelledby="chat-screen-title">
      {/* h1 visivelmente oculto para a11y — o topbar global identifica a tela */}
      <h1 id="chat-screen-title" className="sr-only">
        {t('chat.topbarTitle')}
      </h1>

      {/* ---- Barra de acao superior — Nova conversa (F1-003) ---- */}
      {!isEmpty ? (
        <div className="cs-top-actions">
          <button
            type="button"
            className="cs-new-session-btn"
            onClick={handleNewSession}
            aria-label={t('chat.newSession')}
          >
            <span className="material-symbols-rounded" aria-hidden="true" style={{ fontSize: 16 }}>
              add
            </span>
            {t('chat.newSession')}
          </button>
        </div>
      ) : null}

      {/* ---- Thread scrollavel ---- */}
      <main
        ref={threadRef}
        className="cs-thread"
        id="chat-thread"
        role="log"
        aria-live="polite"
        aria-label={t('chat.topbarTitle')}
        aria-busy={sending}
      >
        {/* Estado vazio — sugestoes iniciais */}
        {isEmpty ? (
          <div className="cs-empty">
            <p className="cs-empty-label">{t('chat.emptyMessages')}</p>
            <ul className="cs-suggestions" role="list">
              {SUGGESTIONS.map((s) => (
                <li key={s}>
                  <button
                    type="button"
                    className="cs-suggestion-chip"
                    onClick={() => handleSuggestion(s)}
                    disabled={sending}
                  >
                    {s}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          messages.map((m, idx) =>
            m.isError === true ? (
              <ChatBubble
                key={m.id}
                message={m}
                activeRef={null}
                onSourceClick={handleSourceClick}
                onConnectSources={handleConnectSources}
                onRetry={() => handleRetry(m.id)}
                onSuggestion={handleSuggestion}
                animIndex={idx}
              />
            ) : (
              <ChatBubble
                key={m.id}
                message={m}
                activeRef={m.role === 'assistant' ? activeRef : null}
                onSourceClick={handleSourceClick}
                onConnectSources={handleConnectSources}
                onSuggestion={handleSuggestion}
                animIndex={idx}
              />
            ),
          )
        )}

        {/* Typing indicator — exibido enquanto aguarda resposta */}
        {sending ? <TypingBubble /> : null}
      </main>

      {/* ---- Barra de input fixa ---- */}
      <div className="cs-input-bar">
        <form className="cs-input-wrap" onSubmit={handleSend}>
          <label htmlFor="chat-field" className="cs-input-label">
            {t('chat.inputLabel')}
          </label>
          <input
            id="chat-field"
            type="text"
            className="cs-input-field"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t('chat.inputPlaceholder')}
            autoComplete="off"
            disabled={sending}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void sendMessage(input.trim());
              }
            }}
          />
          <button
            type="submit"
            className="cs-send-btn"
            aria-label={t('chat.sendAriaLabel')}
            disabled={sending || input.trim().length === 0}
          >
            <span className="material-symbols-rounded" aria-hidden="true">
              send
            </span>
          </button>
        </form>
      </div>

      {/*
       * GAP — painel de sessoes:
       * O prototipo (chat.html) nao exibe a sidebar de sessoes no layout final.
       * A lista de sessoes ({sessions}) foi removida do layout visual mas os
       * dados continuam sendo carregados e a sessao ativa continua controlada
       * pelo estado `activeSession`.
       *
       * Para reintroduzir o painel de sessoes: criar cs-sessions-panel.
       * Mapeamento: sessions={_sessions}, onSelect={setActiveSession},
       * onNew={_handleNewSession}, active={activeSession}.
       * FLAG: sessions panel nao esta no prototipo — aguardar decisao de produto.
       */}
    </section>
  );
}
